/**
 * Layout da cena do Scan Pixel quando o enigma tem muitas pistas (o conteúdo real chegou a 10;
 * o desenho do Figma previa até 5). Funções puras: testáveis sem Phaser.
 */

/** Respiro horizontal do rótulo dentro da aba (soma dos dois lados). */
const FOLGA_ABA = 24;

/**
 * Escala uniforme dos campos de código para a linha inteira caber na área útil.
 * Nunca amplia (máximo 1): com até 5 pistas o desenho continua idêntico ao de antes.
 *
 * @param {number[]} larguras largura natural de cada campo
 * @param {number} vao espaço natural entre campos
 * @param {number} areaUtil largura disponível para a linha
 */
export function escalaDosCampos (larguras, vao, areaUtil) {
    if (!larguras.length) return 1;
    const natural = larguras.reduce((soma, l) => soma + l, 0) + vao * (larguras.length - 1);
    return natural <= areaUtil ? 1 : areaUtil / natural;
}

/**
 * Escala do rótulo das abas: o rótulo é sempre "PISTA N"; quando a aba é estreita demais
 * (o conteúdo real chegou a 10 pistas), a fonte diminui — a mesma escala em todas as abas,
 * calculada pelo rótulo mais largo, para "PISTA 1" e "PISTA 10" ficarem do mesmo tamanho.
 * Nunca amplia (máximo 1).
 *
 * @param {number[]} largurasRotulo largura medida de cada rótulo, em px
 * @param {number} larguraAba largura da aba em px
 */
export function escalaDoRotuloDaAba (largurasRotulo, larguraAba) {
    const maior = Math.max(0, ...largurasRotulo);
    if (maior <= 0) return 1;
    return Math.min(1, (larguraAba - FOLGA_ABA) / maior);
}

/**
 * AP12: ordem das abas do enigma. Cada aba ocupa a posição de exibição sorteada
 * (`ordemExibicao`) e leva ao campo canônico da sua pista — os campos não embaralham,
 * então a palavra do Enigma 5 se forma na ordem certa.
 *
 * @param {{ ordem: number, ordemExibicao?: number }[]} pistas pistas na ordem canônica
 * @returns {{ pista: object, indice: number }[]} abas na ordem de exibição, cada uma com o
 *   índice canônico da pista (o campo que ela preenche)
 */
export function ordemDasAbas (pistas) {
    const exibicao = (pista) => pista.ordemExibicao ?? pista.ordem;
    return pistas
        .map((pista, indice) => ({ pista, indice }))
        .sort((a, b) => exibicao(a.pista) - exibicao(b.pista));
}

/**
 * AP12: número no rótulo da aba. No enigma embaralhado, as abas são numeradas 1..n da esquerda
 * para a direita (a posição na tela) e quem diz o campo é a linha "RESPOSTA NO CAMPO N"; nos
 * demais, o número é o da pista, que coincide com a posição.
 *
 * @param {{ embaralhado?: boolean }} enigma
 * @param {number} posicao posição da aba na tela, da esquerda (0..n-1)
 * @param {{ ordem: number }} pista pista que a aba abre
 */
export function numeroDaAba (enigma, posicao, pista) {
    return enigma?.embaralhado ? posicao + 1 : pista.ordem;
}

/**
 * AP12: pista selecionada ao abrir o enigma. No embaralhado, sempre a primeira aba da esquerda;
 * nos demais, a primeira pista ainda não resolvida (AP9).
 *
 * @param {{ embaralhado?: boolean, pistas: object[] }} enigma
 * @param {number} primeiraPendente índice canônico da primeira pista não resolvida
 * @returns {number} índice canônico da pista a selecionar
 */
export function pistaInicial (enigma, primeiraPendente) {
    if (!enigma?.embaralhado || !enigma.pistas?.length) return primeiraPendente;
    return ordemDasAbas(enigma.pistas)[0].indice;
}

/**
 * AP12: com as abas embaralhadas, a pista informa a posição do campo que preenche
 * ("RESPOSTA NO CAMPO 3"). Devolve o número do campo, ou `null` quando o enigma não está
 * embaralhado (aí a aba já está na posição do campo e o aviso não aparece).
 *
 * @param {{ embaralhado?: boolean, pistas: { ordem: number }[] }} enigma
 * @param {number} indice índice canônico da pista
 * @returns {number | null}
 */
export function campoDaPista (enigma, indice) {
    if (!enigma?.embaralhado) return null;
    return enigma.pistas[indice]?.ordem ?? null;
}

/**
 * AP12 (01/11): no enigma embaralhado, "PISTA N" é a posição da aba, então o campo é rotulado
 * "CAMPO N" — com o mesmo texto nos dois, a aba PISTA 1 que manda para o campo 3 confundia com o
 * campo rotulado PISTA 1. Nos outros enigmas aba e campo têm o mesmo número, e segue "PISTA N".
 *
 * @param {{ embaralhado?: boolean } | null} enigma
 * @returns {'scan.campo' | 'scan.pista'} chave do texto (editável no painel de Textos)
 */
export function chaveDoRotuloDoCampo (enigma) {
    return enigma?.embaralhado ? 'scan.campo' : 'scan.pista';
}

/** Opacidade dos campos que não recebem a resposta da aba escolhida (AP12, 01/11). */
export const ALFA_CAMPO_FORA_DA_PISTA = 0.4;

/**
 * AP12 (01/11): no enigma embaralhado, a aba escolhida aponta o campo da resposta — ele fica
 * nítido e os outros esmaecem. O índice é o canônico da pista, o mesmo do campo que ela preenche.
 * Sem embaralhamento, ou com índice fora da lista, nada esmaece.
 *
 * @param {{ embaralhado?: boolean, pistas: unknown[] } | null} enigma
 * @param {number} indice índice canônico da pista selecionada
 * @returns {number[]} opacidade de cada campo, na ordem dos campos
 */
export function alfaDosCampos (enigma, indice) {
    const total = enigma?.pistas?.length ?? 0;
    const destaca = Boolean(enigma?.embaralhado) && indice >= 0 && indice < total;
    return Array.from({ length: total }, (_, i) => (!destaca || i === indice ? 1 : ALFA_CAMPO_FORA_DA_PISTA));
}

/**
 * Encaixa um bloco de texto na altura disponível: monta no primeiro tamanho de fonte e, se
 * passar da altura, descarta e tenta o próximo (menor). O último tamanho fica mesmo que não
 * caiba. Usado no texto da pista do Scan Pixel, que tem de ficar entre as abas e a instrução
 * "Insira os códigos" — com a linha "RESPOSTA NO CAMPO N" (AP12) e enunciados longos, ele
 * passava por cima da instrução no tablet.
 *
 * @template T
 * @param {(tamanho: number) => { altura: number } & T} montar desenha no tamanho dado
 * @param {(bloco: T) => void} descartar apaga um bloco que não coube
 * @param {number[]} tamanhos tamanhos de fonte, do maior para o menor
 * @param {number} alturaMaxima altura disponível em px
 * @returns {{ altura: number, tamanho: number } & T}
 */
export function encaixarTexto (montar, descartar, tamanhos, alturaMaxima) {
    for (let i = 0; i < tamanhos.length; i++) {
        const bloco = montar(tamanhos[i]);
        if (bloco.altura <= alturaMaxima || i === tamanhos.length - 1) return { ...bloco, tamanho: tamanhos[i] };
        descartar(bloco);
    }
    return null;
}
